import { apiClient } from '@api/apiClient';
import { CHANGE_TRACKING_SOURCES } from '@changeTracking/utils/changeTrackingRegistry';
import { buildChangeTrackingRequestConfig } from '@changeTracking/utils/buildChangeTrackingHeaders';
import {
  LoadBoardApiResponseT,
  LoadboardFetchOptsT,
  PostLoadT,
  PostMultipleResponseT,
  SaveSearchSettingsApiResponseT,
  SavedSearchApiResponseT,
  SavedSearchCategory,
  SavedSearchT,
  loadBoardStore,
} from '@store/loadboard';
import { userStore } from '@store/user/store';
import { ChangeTrackingSource } from '@typeDefinitions/changeTrackingTypes';
import { createError } from '@utils/errorUtils';
import { FeatureFlags } from '@constants/featureFlags';
import { isFeatureFlagEnabled } from '@store/featureFlags/store';

const includeFlaggedEmptyLoadsFilter: Record<string, unknown>[] = [
  {
    field: '',
    filterType: 0,
    values: [],
    group: {
      operator: 'OR',
      filters: [
        {
          field: '',
          filterType: 0,
          values: [],
          group: {
            operator: 'NOT',
            filters: [
              {
                field: 'status',
                filterType: 0,
                values: ['Empty'],
                group: null,
              },
            ],
            groups: [],
          },
        },
        {
          field: '',
          filterType: 0,
          values: [],
          group: {
            operator: 'AND',
            filters: [
              {
                field: 'status',
                filterType: 0,
                values: ['Empty'],
                group: null,
              },
              {
                field: 'orderFlags.flagType',
                filterType: 0,
                values: ['Needs Review'],
                group: null,
              },
            ],
            groups: [],
          },
        },
      ],
      groups: [],
    },
  },
];

export async function fetchLoadBoardItems(
  opts?: LoadboardFetchOptsT,
): Promise<LoadBoardApiResponseT> {
  const { status = ['Empty'], startDate = null, endDate = null } = opts || {};

  let restrictToMyUserId: number | null = null;
  if (loadBoardStore.myItems) {
    restrictToMyUserId = userStore.user.id;
  }

  try {
    const showFlaggedEmptyLoads =
      !loadBoardStore.onlyEmpty &&
      isFeatureFlagEnabled(FeatureFlags.LoadboardEmptyLoads)();
    const filters: Record<string, unknown>[] = showFlaggedEmptyLoads
      ? includeFlaggedEmptyLoadsFilter
      : [
          {
            field: 'status',
            filterType: loadBoardStore.onlyEmpty || opts ? 0 : 1,
            values: status,
          },
        ];

    // Add date filter if start date is provided
    if (startDate) {
      const dateFilter: Record<string, unknown> = {
        field: 'pickDate',
        filterType: endDate && startDate !== endDate ? 'IN_RANGE' : 'EQUALS',
        values: [startDate.toISOString()],
      };

      // If it's a range, add the end date
      if (endDate && startDate !== endDate) {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-call
        (dateFilter.values as string[]).push(endDate.toISOString());
      }

      filters.push(dateFilter);
    }

    const resp = await apiClient.post('/LoadBoard', {
      page: 0,
      pageSize: 5000,
      filters,
      sort: [],
      restrictToMyUserId,
    });
    const value = resp as unknown as LoadBoardApiResponseT;
    const items = value.items.map((order) => {
      order.pickDate = new Date(new Date(order.pickDate).toDateString());
      order.dropDate = new Date(new Date(order.dropDate).toDateString());
      if (
        order.lastCheckCallDate !== null &&
        order.lastCheckCallDate !== undefined
      ) {
        order.lastCheckCallDate = new Date(
          order.lastCheckCallDate.toString().includes('Z')
            ? order.lastCheckCallDate.toString()
            : order.lastCheckCallDate.toString() + 'Z',
        );
      }
      if (
        order.nextCheckCallDateUtc !== null &&
        order.nextCheckCallDateUtc !== undefined
      ) {
        order.nextCheckCallDateUtc = new Date(
          order.nextCheckCallDateUtc.toString().includes('Z')
            ? order.nextCheckCallDateUtc.toString()
            : order.nextCheckCallDateUtc.toString() + 'Z',
        );
      }
      const name = userStore.user.name.trim();
      const myOwnedOrder =
        order.assignedTo === name ||
        order.coveredBy === name ||
        order.owner === name ||
        order.capacityOpsRep === name;
      const myCustomers =
        order.accountManager === name || order.salesManager === name;
      order.my = myOwnedOrder ? 'Yes' : 'No';
      order.myCustomers = myCustomers ? 'Yes' : 'No';
      return order;
    });
    return { ...value, items };
  } catch (error: unknown) {
    throw createError(error, 'Failed to fetch loadboard data');
  }
}

export const fetchSavedSearches = async () => {
  try {
    const resp = (await apiClient.get(
      '/user/savedsearch',
    )) as unknown as SavedSearchApiResponseT;

    if (resp.isFailure) {
      throw new Error(`Fetching saved filters failed: ${resp.error.message}`);
    }

    return resp.value.filter((s) => s.category !== SavedSearchCategory.Legacy);
  } catch (error: unknown) {
    throw createError(error, 'Failed to fetch saved filters with error');
  }
};

export const saveFilter = async (
  model: Record<string, unknown>,
  category: SavedSearchCategory,
  name: string,
) => {
  try {
    const resp = (await apiClient.post('/user/SaveSearchSettings', {
      userId: userStore.user.id,
      Category: category,
      Name: name,
      Params: JSON.stringify(model),
    })) as unknown as SaveSearchSettingsApiResponseT;

    if (resp.isFailure) {
      throw new Error(`Saving filter failed with error: ${resp.error.message}`);
    }

    return resp.value;
  } catch (error: unknown) {
    throw createError(error, 'Failed to post filter with error');
  }
};

export const deleteFilter = async (search: SavedSearchT) => {
  try {
    await apiClient.delete('/user/DeleteSavedSearch', { data: search });
    return true;
  } catch (error: unknown) {
    throw createError(error, 'Failed to delete filter with error');
  }
};

export async function changeStatus(
  id: number,
  status: string,
  changeTrackingSource: ChangeTrackingSource = CHANGE_TRACKING_SOURCES.loadboard
    .statusChange,
) {
  try {
    await apiClient.post(
      '/load/changestatus',
      {
        id: id,
        status: status,
      },
      buildChangeTrackingRequestConfig([changeTrackingSource]),
    );
  } catch (error: unknown) {
    throw createError(error, 'Failed to update status');
  }
}

export async function refreshLoadboardRow(orderId: number) {
  try {
    await apiClient.post(`/LoadBoard/refresh/${orderId}`);
  } catch (error: unknown) {
    throw createError(error, 'Failed to refresh loadboard row');
  }
}

export async function postMultiple(
  loads: PostLoadT[],
  changeTrackingSources: ChangeTrackingSource[] = [],
) {
  try {
    const response = await apiClient.post(
      'posting/PostMultiple',
      {
        LoadPostings: loads,
      },
      buildChangeTrackingRequestConfig(changeTrackingSources),
    );
    return response as unknown as PostMultipleResponseT;
  } catch (error: unknown) {
    throw createError(error, 'Failed to Share Load Capacity');
  }
}

export {
  bulkAddSharedCapacity,
  checkBulkSharedCapacityEligibility,
} from './bulkSharedCapacityApi';

export async function unPostMultiple(
  loadIds: number[],
  changeTrackingSources: ChangeTrackingSource[] = [],
) {
  try {
    const response = await apiClient.post(
      'posting/UnPostMultiple',
      loadIds,
      buildChangeTrackingRequestConfig(changeTrackingSources),
    );
    return response as unknown as PostMultipleResponseT;
  } catch (error: unknown) {
    throw createError(error, 'Failed to Share Load Capacity');
  }
}
